import { useCallback, useEffect, useRef, useState } from 'react'
import { OLLIE_ORDERING_QUANTITY_DEBOUNCE_MS } from 'src/utils/ollieOrderingProposal'

/**
 * Stepper taps land on screen straight away and travel together: the number
 * moves on every tap, and one correction goes out once the chef settles, so
 * "I need twelve" is one request rather than eleven. Only the line being saved
 * is disabled — the rest of the proposed order stays tappable.
 *
 * Confirm calls flush() first, so a tap made a moment before the button is
 * pressed is still in the basket write.
 */
function useOllieOrderingQuantityEdits({
  onEditQuantity,
  debounceMs = OLLIE_ORDERING_QUANTITY_DEBOUNCE_MS,
} = {}) {
  const [shown, setShown] = useState({})
  const [saving, setSaving] = useState({})
  const timers = useRef({})
  const queued = useRef({})
  const inFlight = useRef(Promise.resolve())
  const alive = useRef(true)

  useEffect(
    () => () => {
      alive.current = false
      Object.values(timers.current).forEach(timer => clearTimeout(timer))
      timers.current = {}
    },
    [],
  )

  const save = useCallback(
    key => {
      const timer = timers.current[key]
      if (timer) {
        clearTimeout(timer)
        delete timers.current[key]
      }

      const edit = queued.current[key]
      if (!edit) return inFlight.current

      delete queued.current[key]
      setSaving(current => ({ ...current, [key]: true }))

      // One correction at a time: Express applies changes to a single stored
      // proposal, so overlapping saves would fight over it.
      const run = inFlight.current
        .catch(() => null)
        .then(() =>
          onEditQuantity?.({
            phrase: edit.phrase,
            quantity: edit.quantity,
            quiet: true,
          }),
        )
        .catch(() => null)
        .then(result => {
          if (!alive.current) return result
          setSaving(current => {
            const next = { ...current }
            delete next[key]
            return next
          })
          // Drop the local number once the saved proposal is back, so a
          // refused edit shows what Ollie actually holds rather than a
          // quantity that was never stored.
          setShown(current => {
            const next = { ...current }
            delete next[key]
            return next
          })
          return result
        })

      inFlight.current = run.catch(() => null)
      return run
    },
    [onEditQuantity],
  )

  const setQuantity = useCallback(
    ({ key, phrase, quantity } = {}) => {
      const amount = Number(quantity)
      if (!key || !Number.isFinite(amount) || amount <= 0) return

      setShown(current => ({ ...current, [key]: amount }))
      queued.current[key] = { phrase, quantity: amount }

      if (timers.current[key]) clearTimeout(timers.current[key])
      timers.current[key] = setTimeout(() => save(key), debounceMs)
    },
    [debounceMs, save],
  )

  const flush = useCallback(async () => {
    Object.keys(queued.current).forEach(key => save(key))
    await inFlight.current.catch(() => null)
  }, [save])

  const quantityFor = useCallback(
    (key, fallback) => (key in shown ? shown[key] : fallback),
    [shown],
  )

  const isSaving = useCallback(key => Boolean(saving[key]), [saving])

  return { quantityFor, isSaving, setQuantity, flush }
}

export default useOllieOrderingQuantityEdits
